/**
 * MobileBottomNav — 5-slot bottom navigation, mobile only.
 * Desktop uses <DesktopNav /> mounted separately under the header.
 *
 * Blinkit-style hide-on-scroll:
 *   • Slides down on scroll-down past ~80px
 *   • Slides back up on scroll-up
 *   • Always visible at page top
 */
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, LayoutGrid, Flame, ShoppingCart, ClipboardList } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

const ITEMS = [
  { to: "/",               label: "Home",      icon: Home,           testId: "bnav-home" },
  { to: "/category",       label: "Shop",      icon: LayoutGrid,     testId: "bnav-shop" },
  { to: "/deal-baazar",    label: "Deal Baazar", icon: Flame,       testId: "bnav-deal-baazar", forceRed: true },
  { to: "/cart",           label: "Cart",      icon: ShoppingCart,   testId: "bnav-cart",     badge: "cart" },
  { to: "/account/orders", label: "My Orders", icon: ClipboardList,  testId: "bnav-orders" },
];

export default function MobileBottomNav() {
  const loc = useLocation();
  const { itemCount } = useCart() || { itemCount: 0 };
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  // Hide on scroll-down (past 80px), reveal on scroll-up. Passive listener.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      const delta = y - lastY.current;
      if (y < 80) { setHidden(false); }
      else if (delta > 6) { setHidden(true); }
      else if (delta < -6) { setHidden(false); }
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reset visibility on route change
  useEffect(() => { setHidden(false); lastY.current = 0; }, [loc.pathname]);

  if (loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/pos") || loc.pathname.startsWith("/delivery")) return null;

  return (
    <nav
      className={`md:hidden fixed bottom-0 left-0 right-0 z-30 bg-card/95 backdrop-blur border-t transition-transform duration-300 ease-out ${hidden ? "translate-y-full" : "translate-y-0"}`}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="mobile-bottom-nav"
    >
      <div className="grid grid-cols-5">
        {ITEMS.map(({ to, label, icon: Icon, testId, badge, forceRed }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              `relative flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition ${
                isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`
            }
            data-testid={testId}
          >
            <div className="relative">
              <Icon className={`w-5 h-5 ${forceRed ? "text-primary" : ""}`} />
              {badge === "cart" && itemCount > 0 && (
                <span className="absolute -top-1.5 -right-2.5 min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-mono font-bold grid place-items-center">
                  {itemCount > 99 ? "99+" : itemCount}
                </span>
              )}
            </div>
            <span>{label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
